import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { adminShopAPI } from '../services/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { getAdminTenantId } from '../utils/tenantHelper';

const ACCENT = '#043663';
const ACCENT_SOFT = '#e8eef4';
const PAGE = 10;

const Page = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem 1.5rem 1.25rem;
  min-height: 70vh;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.02em;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PillBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 36px;
  padding: 0 0.95rem;
  border-radius: 999px;
  border: ${(p) => (p.$ghost ? '1px solid #e5e7eb' : 'none')};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$danger ? '#fef2f2' : p.$primary ? ACCENT : p.$ghost ? '#fff' : ACCENT_SOFT)};
  color: ${(p) => (p.$danger ? '#dc2626' : p.$primary ? '#fff' : ACCENT)};
  &:hover {
    background: ${(p) =>
      p.$danger ? '#fee2e2' : p.$primary ? '#032a4a' : p.$ghost ? '#f9fafb' : '#dce6ef'};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  border: none;
  background: ${(p) => (p.$active ? ACCENT_SOFT : 'transparent')};
  color: ${(p) => (p.$active ? ACCENT : '#6b7280')};
  font-weight: 600;
  font-size: 0.875rem;
  padding: 0.55rem 0.9rem;
  border-radius: 10px;
  cursor: pointer;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
  color: #6b7280;
  font-size: 0.875rem;
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
`;

const Select = styled.select`
  height: 34px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 0.5rem;
  background: #fff;
  color: #374151;
  font-size: 0.8125rem;
`;

const Search = styled.input`
  height: 34px;
  width: 210px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 0.75rem 0 2rem;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' fill='none' stroke='%239ca3af' stroke-width='2' viewBox='0 0 24 24'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M20 20l-3-3'/%3E%3C/svg%3E") 10px center no-repeat;
  font-size: 0.8125rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 860px;
  th {
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    color: #9ca3af;
    padding: 0.7rem 0.65rem;
    border-bottom: 1px solid #f3f4f6;
    white-space: nowrap;
  }
  td {
    padding: 0.7rem 0.65rem;
    border-bottom: 1px solid #f3f4f6;
    font-size: 0.875rem;
    color: #1f2937;
    vertical-align: middle;
  }
  tbody tr:hover {
    background: #fafafa;
  }
  tbody tr.selected {
    background: #f3f7fb;
  }
  tbody tr.selected:hover {
    background: #e8eef4;
  }
`;

const Check = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: ${ACCENT};
  cursor: pointer;
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
  padding: 0.6rem 0.85rem;
  background: ${ACCENT_SOFT};
  border: 1px solid #d5e0ea;
  border-radius: 12px;
`;

const BulkLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  color: ${ACCENT};
  font-size: 0.875rem;
  font-weight: 600;
`;

const BulkActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const LinkBtn = styled.button`
  border: none;
  background: none;
  color: ${ACCENT};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const ConfirmBox = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(4, 54, 99, 0.18);
  padding: 1.25rem 1.35rem 1.2rem;
`;

const ConfirmTitle = styled.h2`
  margin: 0 0 0.45rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
`;

const ConfirmCopy = styled.p`
  margin: 0 0 1.1rem;
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.45;
`;

const SettingCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1.1rem 1.15rem 1.2rem;
  background: #f9fafb;
  margin-bottom: 1rem;
`;

const SettingHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const SettingTitle = styled.h2`
  margin: 0 0 0.2rem;
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
`;

const SettingCopy = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.45;
  max-width: 520px;
`;

const QuietTag = styled.span`
  display: inline-block;
  margin-top: 0.2rem;
  font-size: 0.68rem;
  font-weight: 600;
  color: ${ACCENT};
  background: ${ACCENT_SOFT};
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
`;

const NameCell = styled.div`
  font-weight: 500;
  max-width: 280px;
`;

const Thumb = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f3f4f6 center/cover no-repeat;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ThumbWrap = styled.div`
  position: relative;
  width: 36px;
`;

const ThumbCount = styled.span`
  position: absolute;
  right: -7px;
  bottom: -7px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: ${ACCENT};
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
  gap: 0.75rem;
`;

const GalleryCard = styled.div`
  position: relative;
  border: 1px solid ${(p) => (p.$primary ? ACCENT : p.$over ? ACCENT : '#e5e7eb')};
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: ${(p) =>
    p.$dragging
      ? '0 16px 32px rgba(15, 23, 42, 0.18)'
      : p.$over
        ? '0 0 0 3px rgba(4, 54, 99, 0.14)'
        : p.$hero
          ? '0 8px 20px rgba(4, 54, 99, 0.1)'
          : '0 1px 2px rgba(15, 23, 42, 0.04)'};
  transform: ${(p) => (p.$dragging ? 'scale(1.03)' : 'none')};
  opacity: ${(p) => (p.$dragging ? 0.92 : 1)};
  cursor: ${(p) => (p.$canDrag ? (p.$dragging ? 'grabbing' : 'grab') : 'default')};
  user-select: none;
  touch-action: ${(p) => (p.$canDrag ? 'none' : 'auto')};
  transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
  z-index: ${(p) => (p.$dragging ? 2 : 1)};
  &:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 2px;
  }
`;

const GalleryPhoto = styled.div`
  position: relative;
  aspect-ratio: 1;
  background: #eef2f6;
  overflow: hidden;
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const GalleryName = styled.div`
  padding: 0.4rem 0.55rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #4b5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const GalleryActions = styled.div`
  display: flex;
  gap: 0.25rem;
  padding: 0 0.45rem 0.45rem;
`;

const GalleryBtn = styled.button`
  flex: 1;
  height: 26px;
  border: none;
  border-radius: 7px;
  font-size: 0.68rem;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$primary ? ACCENT : ACCENT_SOFT)};
  color: ${(p) => (p.$primary ? '#fff' : ACCENT)};
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GalleryX = styled.button`
  position: absolute;
  top: 7px;
  right: 7px;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.72);
  color: #fff;
  font-size: 0.95rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0 1px;
  &:hover {
    background: rgba(17, 24, 39, 0.9);
  }
`;

const MainBadge = styled.span`
  position: absolute;
  top: 7px;
  left: 7px;
  background: ${ACCENT};
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
`;

const IndexBadge = styled.span`
  position: absolute;
  bottom: 7px;
  left: 7px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.72);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
`;

const Grip = styled.span`
  position: absolute;
  bottom: 7px;
  right: 7px;
  width: 22px;
  height: 22px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.92);
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
  svg {
    display: block;
  }
`;

const GalleryHint = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
`;

const PhotoHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
`;

const PhotoCount = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
  white-space: nowrap;
`;

const SavingNote = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  color: ${ACCENT};
`;

const NewBadge = styled.span`
  position: absolute;
  top: 7px;
  left: 7px;
  background: #fff;
  color: ${ACCENT};
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
`;

const DropZone = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-height: 148px;
  padding: 1.5rem 1.25rem;
  border: 1.5px dashed ${(p) => (p.$over ? ACCENT : '#d1d5db')};
  border-radius: 16px;
  background: ${(p) => (p.$over ? '#f3f7fb' : '#f8fafc')};
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover {
    border-color: ${ACCENT};
    background: #f3f7fb;
  }
`;

const HiddenFile = styled.input`
  display: none;
`;

const UploadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 36px;
  padding: 0 1rem;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  svg {
    display: block;
  }
`;

const DropTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
`;

const DropHint = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const Toggle = styled.button`
  width: 36px;
  height: 20px;
  border: none;
  border-radius: 999px;
  background: ${(p) => (p.$on ? ACCENT : '#d1d5db')};
  position: relative;
  cursor: pointer;
  padding: 0;
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$on ? '18px' : '2px')};
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.15s ease;
  }
`;

const Kebab = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  border-radius: 8px;
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Menu = styled.div`
  position: fixed;
  top: ${(p) => p.$top}px;
  left: ${(p) => p.$left}px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  min-width: 148px;
  z-index: 10000;
  padding: 0.35rem;
  button {
    width: 100%;
    text-align: left;
    border: none;
    background: none;
    padding: 0.5rem 0.65rem;
    border-radius: 8px;
    font-size: 0.8125rem;
    cursor: pointer;
    color: #374151;
    &:hover {
      background: #f3f4f6;
    }
    &.danger {
      color: #dc2626;
    }
  }
`;

const Pager = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.35rem;
  padding: 1.25rem 0 0.25rem;
`;

const PageBtn = styled.button`
  min-width: 32px;
  height: 32px;
  border: none;
  border-radius: 999px;
  background: ${(p) => (p.$active ? ACCENT : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : '#6b7280')};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Empty = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: #6b7280;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 250px;
  background: rgba(4, 54, 99, 0.45);
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  overflow: hidden;
  @media (max-width: 768px) {
    left: 0;
  }
`;

const Modal = styled.div`
  width: 100%;
  max-width: 760px;
  max-height: calc(100vh - 3rem);
  overflow-y: auto;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(4, 54, 99, 0.18);
`;

const ModalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.15rem 1.35rem 0.85rem;
  border-bottom: 1px solid #f3f4f6;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #111827;
`;

const CloseBtn = styled.button`
  border: none;
  background: #f3f4f6;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  cursor: pointer;
  color: #4b5563;
  font-size: 1.1rem;
`;

const ModalBody = styled.form`
  padding: 1.2rem 1.35rem 1.35rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem 1rem;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  &.full {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  height: 40px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0 0.75rem;
  font-size: 0.875rem;
`;

const TextArea = styled.textarea`
  min-height: 180px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.75rem;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.45;
  resize: vertical;
`;

const FieldSelect = styled.select`
  height: 40px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0 0.75rem;
  font-size: 0.875rem;
  background: #fff;
`;

const Checks = styled.div`
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
  label {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8125rem;
    color: #374151;
  }
`;

const ModalFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.15rem;
`;

const GhostBtn = styled.button`
  height: 38px;
  padding: 0 1rem;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
`;

const VariantBox = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.9rem 1rem 1rem;
  background: #f9fafb;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.55rem 0 0.7rem;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #dbe3ec;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${ACCENT};
  button {
    border: none;
    background: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    font-size: 0.95rem;
  }
`;

const Swatch = styled.i`
  width: ${(p) => p.$size || 10}px;
  height: ${(p) => p.$size || 10}px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  border: 1px solid ${(p) => (p.$light ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.08)')};
  display: inline-block;
  flex-shrink: 0;
  box-shadow: ${(p) => (p.$light ? 'inset 0 0 0 1px rgba(0,0,0,0.06)' : 'none')};
`;

const SuggestBtn = styled.button`
  height: 28px;
  padding: 0 0.65rem;
  border-radius: 999px;
  border: 1px ${(p) => (p.$active ? 'solid' : 'dashed')} ${(p) => (p.$active ? ACCENT : '#c5d0db')};
  background: ${(p) => (p.$active ? ACCENT_SOFT : 'transparent')};
  color: ${(p) => (p.$active ? ACCENT : '#6b7280')};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    border-color: ${ACCENT};
    color: ${ACCENT};
  }
`;

const SelectedSwatches = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin: 0.55rem 0 0.75rem;
`;

const SelectedSwatch = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 52px;
  position: relative;
  span {
    margin-top: 0.3rem;
    font-size: 0.68rem;
    font-weight: 600;
    color: #4b5563;
    text-align: center;
    line-height: 1.15;
    max-width: 52px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SwatchRemove = styled.button`
  position: absolute;
  top: -5px;
  right: 2px;
  width: 16px;
  height: 16px;
  border: none;
  border-radius: 50%;
  background: #111827;
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  padding: 0;
  cursor: pointer;
`;

const SwatchGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 0.7rem;
  padding: 0.7rem 0.75rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
`;

const SwatchPick = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  background: ${(p) => p.$color};
  border: 1px solid ${(p) => (p.$light ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.08)')};
  outline: ${(p) => (p.$selected ? `2px solid ${ACCENT}` : 'none')};
  outline-offset: 3px;
  position: relative;
  box-shadow: ${(p) => (p.$light ? 'inset 0 0 0 1px rgba(0,0,0,0.06)' : 'none')};
  &:hover {
    transform: scale(1.06);
  }
  &::after {
    content: '${(p) => (p.$selected ? '✓' : '')}';
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: 700;
    color: ${(p) => (p.$light ? '#111827' : '#fff')};
  }
`;

const CustomColourRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-top: 0.7rem;
  flex-wrap: wrap;
  input[type='color'] {
    width: 36px;
    height: 36px;
    padding: 0;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
  }
`;

const SwatchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.25rem;
  flex-wrap: wrap;
`;

const VariantTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.75rem;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  th,
  td {
    padding: 0.45rem 0.5rem;
    border-bottom: 1px solid #f3f4f6;
    font-size: 0.75rem;
    text-align: left;
  }
  th {
    color: #9ca3af;
    font-weight: 600;
  }
  input[type='text'],
  input[type='number'] {
    width: 100%;
    height: 32px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0 0.45rem;
    font-size: 0.75rem;
  }
`;

const Hint = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: #6b7280;
`;

const SIZE_PRESETS = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const COLOUR_PALETTE = [
  { name: 'Black', hex: '#111111' },
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Grey', hex: '#9CA3AF' },
  { name: 'Silver', hex: '#C0C0C0' },
  { name: 'Navy', hex: '#043663' },
  { name: 'Blue', hex: '#2563EB' },
  { name: 'Sky', hex: '#38BDF8' },
  { name: 'Teal', hex: '#0D9488' },
  { name: 'Green', hex: '#16A34A' },
  { name: 'Olive', hex: '#4D7C0F' },
  { name: 'Mint', hex: '#6EE7B7' },
  { name: 'Yellow', hex: '#EAB308' },
  { name: 'Gold', hex: '#D4AF37' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'Coral', hex: '#F97066' },
  { name: 'Red', hex: '#DC2626' },
  { name: 'Burgundy', hex: '#7F1D1D' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Lavender', hex: '#C4B5FD' },
  { name: 'Purple', hex: '#7C3AED' },
  { name: 'Brown', hex: '#92400E' },
  { name: 'Beige', hex: '#D6C7B0' },
  { name: 'Khaki', hex: '#C4B58A' },
  { name: 'Cream', hex: '#F5F0E6' },
];

function hexKey(hex) {
  return String(hex || '')
    .replace('#', '')
    .toLowerCase();
}

function normalizeHex(hex) {
  const s = String(hex || '').trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s.toUpperCase();
  if (/^[0-9A-Fa-f]{6}$/.test(s)) return `#${s.toUpperCase()}`;
  return null;
}

function hexToRgb(hex) {
  const h = hexKey(hex);
  if (h.length !== 6) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function isLightHex(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000 > 168;
}

function swatchFrom(name, hex) {
  const normalized = normalizeHex(hex);
  if (normalized) {
    const byHex = COLOUR_PALETTE.find((c) => hexKey(c.hex) === hexKey(normalized));
    if (byHex) return byHex;
  }
  const byName = COLOUR_PALETTE.find(
    (c) => c.name.toLowerCase() === String(name || '').trim().toLowerCase()
  );
  if (byName) return byName;
  if (normalized) {
    return { name: normalized, hex: normalized };
  }
  return name ? { name: String(name).trim(), hex: '#9CA3AF' } : null;
}

const MAX_PRODUCT_IMAGES = 8;

function productImages(p) {
  const images = Array.isArray(p?.images) ? p.images.filter((img) => img?.url) : [];
  if (images.length) return images;
  if (p?.imageUrl) {
    return [{ id: null, url: p.imageUrl, isPrimary: true, sortOrder: 0 }];
  }
  return [];
}

function moveItem(list, from, to) {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function GripIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <circle cx="4" cy="3" r="1.05" fill="currentColor" />
      <circle cx="8" cy="3" r="1.05" fill="currentColor" />
      <circle cx="4" cy="6" r="1.05" fill="currentColor" />
      <circle cx="8" cy="6" r="1.05" fill="currentColor" />
      <circle cx="4" cy="9" r="1.05" fill="currentColor" />
      <circle cx="8" cy="9" r="1.05" fill="currentColor" />
    </svg>
  );
}

function PendingThumb({
  file,
  onRemove,
  index,
  label,
  canDrag,
  dragging,
  over,
  hero,
  onDragStart,
  onKeyMove,
}) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return (
    <GalleryCard
      data-gallery-kind="pending"
      data-gallery-index={index}
      $canDrag={canDrag}
      $dragging={dragging}
      $over={over}
      $hero={hero}
      $primary={hero}
      tabIndex={canDrag ? 0 : undefined}
      aria-grabbed={dragging ? 'true' : 'false'}
      aria-label={`${file.name}, photo ${label}. ${canDrag ? 'Drag or use arrow keys to reorder.' : ''}`}
      onPointerDown={onDragStart}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          onKeyMove?.(-1);
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          onKeyMove?.(1);
        }
      }}
    >
      <GalleryPhoto>
        {url ? <img src={url} alt="" draggable={false} /> : null}
        {hero ? <MainBadge>Cover</MainBadge> : <NewBadge>New</NewBadge>}
        <GalleryX type="button" aria-label={`Remove ${file.name}`} onClick={onRemove}>
          ×
        </GalleryX>
        <IndexBadge>{label}</IndexBadge>
        {canDrag ? (
          <Grip>
            <GripIcon />
          </Grip>
        ) : null}
      </GalleryPhoto>
      <GalleryName title={file.name}>{hero ? 'Cover · unsaved' : file.name}</GalleryName>
    </GalleryCard>
  );
}

const emptyProduct = () => ({
  name: '',
  description: '',
  categoryId: '',
  priceChf: '',
  compareAtChf: '',
  stockQuantity: '0',
  sku: '',
  trackStock: true,
  isActive: true,
  freeShipping: false,
  sortOrder: '0',
  sizes: [],
  colours: [],
  variants: [],
  sizeDraft: '',
});

function normalizeOption(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function uniqueOptions(list) {
  const seen = new Set();
  const out = [];
  list.forEach((item) => {
    const value = normalizeOption(item);
    const key = value.toLowerCase();
    if (!value || seen.has(key)) return;
    seen.add(key);
    out.push(value);
  });
  return out;
}

function skuBase(name, sku) {
  if (sku && String(sku).trim()) {
    return String(sku).trim().toUpperCase().replace(/\s+/g, '-');
  }
  return (
    String(name || 'HPS')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 12) || 'HPS'
  );
}

function suggestVariantSku(base, size, colour) {
  const bits = [base];
  if (size) bits.push(String(size).toUpperCase().replace(/\s+/g, ''));
  const colourName = colour && typeof colour === 'object' ? colour.name : colour;
  if (colourName) bits.push(String(colourName).toUpperCase().replace(/\s+/g, '').slice(0, 3));
  return bits.join('-');
}

function colourKey(swatch) {
  if (!swatch) return '';
  if (typeof swatch === 'string') return swatch.toLowerCase();
  return hexKey(swatch.hex) || String(swatch.name || '').toLowerCase();
}

function variantComboKey(size, swatch) {
  return `${String(size || '').toLowerCase()}|${colourKey(swatch)}`;
}

function coloursFromVariants(variants) {
  const out = [];
  const seen = new Set();
  (variants || []).forEach((v) => {
    if (!v.colour) return;
    const swatch = swatchFrom(v.colour, v.colourHex);
    const key = colourKey(swatch);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(swatch);
  });
  return out;
}

function makeVariantRow(size, swatch, previous, productSku, productName) {
  const found = (previous || []).find(
    (v) => variantComboKey(v.size, { name: v.colour, hex: v.colourHex }) === variantComboKey(size, swatch)
  );
  return {
    id: found?.id || null,
    size: size || null,
    colour: swatch?.name || null,
    colourHex: swatch?.hex || found?.colourHex || null,
    sku: found?.sku || suggestVariantSku(skuBase(productName, productSku), size, swatch),
    stockQuantity: found?.stockQuantity != null ? String(found.stockQuantity) : '0',
    priceChf: found?.priceChf != null && found.priceChf !== '' ? String(found.priceChf) : '',
    isActive: found?.isActive !== false,
  };
}

function formFromProduct(p) {
  const variants = (p.variants || []).map((v) => {
    const swatch = swatchFrom(v.colour, v.colourHex);
    return {
      id: v.id,
      size: v.size || null,
      colour: swatch?.name || v.colour || null,
      colourHex: swatch?.hex || v.colourHex || null,
      sku: v.sku || '',
      stockQuantity: String(v.stockQuantity ?? 0),
      priceChf: v.priceChf != null ? String(v.priceChf) : '',
      isActive: v.isActive !== false,
    };
  });
  const fromApi = (p.colourSwatches || []).map((c) => swatchFrom(c.name, c.hex)).filter(Boolean);
  const fromVariants = [];
  const seen = new Set();
  variants.forEach((v) => {
    if (!v.colour) return;
    const swatch = swatchFrom(v.colour, v.colourHex);
    const key = colourKey(swatch);
    if (!key || seen.has(key)) return;
    seen.add(key);
    fromVariants.push(swatch);
  });
  return {
    name: p.name || '',
    description: p.description || '',
    categoryId: p.categoryId || '',
    priceChf: String(p.priceChf ?? ''),
    compareAtChf: p.compareAtChf != null ? String(p.compareAtChf) : '',
    stockQuantity: String(p.stockQuantity ?? 0),
    sku: p.sku || '',
    trackStock: p.trackStock !== false,
    isActive: !!p.isActive,
    freeShipping: !!p.freeShipping,
    sortOrder: String(p.sortOrder ?? 0),
    sizes: p.sizes?.length ? p.sizes : uniqueOptions(variants.map((v) => v.size)),
    colours: fromApi.length ? fromApi : fromVariants,
    variants,
    sizeDraft: '',
  };
}

function money(n) {
  return `CHF ${Number(n || 0).toFixed(2)}`;
}

const emptyShipping = () => ({
  deliveryEnabled: true,
  pickupEnabled: false,
  deliveryFeeChf: '7.5',
  freeShippingMinChf: '',
  pickupName: '',
  pickupStreet: '',
  pickupPostalCode: '',
  pickupCity: '',
  pickupCountry: 'CH',
  pickupNotes: '',
});

function shippingFromApi(d) {
  if (!d) return emptyShipping();
  return {
    deliveryEnabled: d.deliveryEnabled !== false,
    pickupEnabled: d.pickupEnabled !== false,
    deliveryFeeChf: String(d.deliveryFeeChf ?? 7.5),
    freeShippingMinChf: d.freeShippingMinChf != null ? String(d.freeShippingMinChf) : '',
    pickupName: d.pickupName || '',
    pickupStreet: d.pickupStreet || '',
    pickupPostalCode: d.pickupPostalCode || '',
    pickupCity: d.pickupCity || '',
    pickupCountry: d.pickupCountry || 'CH',
    pickupNotes: d.pickupNotes || '',
  };
}

function pageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items = [1];
  if (current > 3) items.push('…');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i += 1) {
    items.push(i);
  }
  if (current < total - 2) items.push('…');
  items.push(total);
  return items;
}

export default function AdminShop() {
  const { adminUser } = useAdminAuth();
  const canCreate = adminUser?.isSuperAdmin || adminUser?.permissions?.shop?.create;
  const canEdit = adminUser?.isSuperAdmin || adminUser?.permissions?.shop?.edit;
  const canDelete = adminUser?.isSuperAdmin || adminUser?.permissions?.shop?.delete;
  const queryClient = useQueryClient();
  const tenantId = getAdminTenantId();

  const [shopView, setShopView] = useState('products');
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE);
  const [productOpen, setProductOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyProduct());
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [galleryDrag, setGalleryDrag] = useState(null);
  const [menu, setMenu] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const imageInputRef = useRef(null);
  const headerCheckRef = useRef(null);
  const lastCheckedRef = useRef(null);
  const galleryDragRef = useRef(null);
  const existingImagesRef = useRef([]);
  const persistTimerRef = useRef(null);
  existingImagesRef.current = existingImages;
  const [shippingForm, setShippingForm] = useState(emptyShipping());
  const [shippingContactEmail, setShippingContactEmail] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [customHex, setCustomHex] = useState('#043663');
  const [applySizes, setApplySizes] = useState([]);
  const menuRef = useRef(null);

  const { data: categoriesRes } = useQuery(
    ['admin-shop-categories', tenantId],
    () => adminShopAPI.getCategories(),
    { staleTime: 15_000, enabled: !!tenantId }
  );
  const { data: productsRes, isLoading } = useQuery(
    ['admin-shop-products', tenantId],
    () => adminShopAPI.getProducts(),
    { staleTime: 15_000, enabled: !!tenantId }
  );
  const { data: shippingRes } = useQuery(
    ['admin-shop-shipping', tenantId],
    () => adminShopAPI.getShipping(),
    { staleTime: 15_000, enabled: !!tenantId }
  );

  const categories = categoriesRes?.data || [];
  const products = productsRes?.data || [];

  useEffect(() => {
    const d = shippingRes?.data || shippingRes;
    if (!d || typeof d !== 'object') return;
    if (d.deliveryFeeChf == null && d.deliveryEnabled == null) return;
    setShippingForm(shippingFromApi(d));
    setShippingContactEmail(d.contactEmail || '');
  }, [shippingRes]);

  useEffect(() => {
    setSelectedIds((prev) => {
      if (!prev.size) return prev;
      const have = new Set(products.map((p) => p.id));
      const next = new Set([...prev].filter((id) => have.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [products]);

  useEffect(() => {
    if (!productOpen || !editingId) return;
    if (galleryDragRef.current) return;
    const current = products.find((item) => item.id === editingId);
    if (current) setExistingImages(productImages(current));
  }, [products, editingId, productOpen]);

  const invalidate = () => {
    queryClient.invalidateQueries(['admin-shop-categories']);
    queryClient.invalidateQueries(['admin-shop-products']);
  };

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(null);
    };
    const close = () => setMenu(null);
    document.addEventListener('click', onDoc);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', onDoc);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, []);

  useEffect(() => {
    const open = productOpen || categoryOpen || !!bulkConfirm;
    if (!open) return undefined;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [productOpen, categoryOpen, bulkConfirm]);

  const addImageFiles = (list) => {
    const picked = Array.from(list || []).filter((file) => {
      const type = String(file.type || '');
      const name = String(file.name || '').toLowerCase();
      const isImage =
        type.startsWith('image/') || /\.(png|jpe?g|webp|gif|bmp)$/.test(name);
      if (!isImage) {
        toast.error(`${file.name || 'That file'} is not an image.`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 5 MB.`);
        return false;
      }
      return true;
    });
    if (!picked.length) return;
    const room = MAX_PRODUCT_IMAGES - existingImages.length - imageFiles.length;
    if (room <= 0) {
      toast.error(`A product can have at most ${MAX_PRODUCT_IMAGES} photos.`);
      return;
    }
    if (picked.length > room) {
      toast.error(`Only ${room} more photo${room === 1 ? '' : 's'} can be added.`);
    }
    setImageFiles((prev) => [...prev, ...picked.slice(0, room)]);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (tab === 'active' && !p.isActive) return false;
      if (tab === 'inactive' && p.isActive) return false;
      if (tab === 'oos' && p.inStock) return false;
      if (categoryFilter && p.categoryId !== categoryFilter) return false;
      if (!q) return true;
      const variantHit = (p.variants || []).some((v) =>
        [v.sku, v.size, v.colour].some((val) => String(val || '').toLowerCase().includes(q))
      );
      return (
        String(p.name || '').toLowerCase().includes(q) ||
        String(p.sku || '').toLowerCase().includes(q) ||
        String(p.categoryName || '').toLowerCase().includes(q) ||
        (p.sizes || []).some((s) => String(s).toLowerCase().includes(q)) ||
        (p.colours || []).some((c) => String(c).toLowerCase().includes(q)) ||
        variantHit
      );
    });
  }, [products, tab, search, categoryFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const slice = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const from = filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(filtered.length, safePage * pageSize);
  const pageIds = slice.map((p) => p.id);
  const selectedCount = selectedIds.size;
  const selectedOnPage = pageIds.filter((id) => selectedIds.has(id)).length;
  const allPageSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;
  const somePageSelected = selectedOnPage > 0 && !allPageSelected;
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));
  const selectedProducts = products.filter((p) => selectedIds.has(p.id));
  const canBulkActivate = selectedProducts.some((p) => !p.isActive);
  const canBulkDeactivate = selectedProducts.some((p) => p.isActive);

  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = somePageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const saveProduct = useMutation(
    async ({ payload, id, files }) => {
      const saved = id
        ? await adminShopAPI.updateProduct(id, payload)
        : await adminShopAPI.createProduct(payload);
      const product = saved?.data || saved;
      if (files?.length && product?.id) {
        for (const file of files) {
          const fd = new FormData();
          fd.append('image', file);
          await adminShopAPI.uploadImage(product.id, fd);
        }
      }
      return product;
    },
    {
      onSuccess: () => {
        toast.success(editingId ? 'Product updated' : 'Product created');
        closeProduct();
        invalidate();
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Could not save product'),
    }
  );

  const toggleActive = useMutation(
    ({ id, isActive }) => adminShopAPI.updateProduct(id, { isActive }),
    {
      onSuccess: () => invalidate(),
      onError: (err) => toast.error(err.response?.data?.message || 'Could not update status'),
    }
  );

  const deleteProduct = useMutation((id) => adminShopAPI.deleteProduct(id), {
    onSuccess: () => {
      toast.success('Product deleted');
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Could not delete product'),
  });

  const saveShipping = useMutation((payload) => adminShopAPI.updateShipping(payload), {
    onSuccess: (res) => {
      const d = res?.data || res;
      if (d) {
        setShippingForm(shippingFromApi(d));
        setShippingContactEmail(d.contactEmail || '');
      }
      toast.success('Shipping settings saved');
      queryClient.invalidateQueries(['admin-shop-shipping']);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Could not save shipping'),
  });

  const onSaveShipping = (e) => {
    e?.preventDefault?.();
    if (!shippingForm.deliveryEnabled && !shippingForm.pickupEnabled) {
      toast.error('Enable home delivery, pickup, or both.');
      return;
    }
    const fee = Number(shippingForm.deliveryFeeChf);
    if (!Number.isFinite(fee) || fee < 0) {
      toast.error('Enter a delivery fee of CHF 0 or more.');
      return;
    }
    if (shippingForm.pickupEnabled) {
      if (
        !shippingForm.pickupName.trim() ||
        !shippingForm.pickupStreet.trim() ||
        !shippingForm.pickupPostalCode.trim() ||
        !shippingForm.pickupCity.trim()
      ) {
        toast.error('Pickup needs a location name, street, postal code and city.');
        return;
      }
    }
    saveShipping.mutate({
      deliveryEnabled: !!shippingForm.deliveryEnabled,
      pickupEnabled: !!shippingForm.pickupEnabled,
      deliveryFeeChf: fee,
      freeShippingMinChf: shippingForm.freeShippingMinChf === '' ? null : shippingForm.freeShippingMinChf,
      pickupName: shippingForm.pickupName.trim(),
      pickupStreet: shippingForm.pickupStreet.trim(),
      pickupPostalCode: shippingForm.pickupPostalCode.trim(),
      pickupCity: shippingForm.pickupCity.trim(),
      pickupCountry: shippingForm.pickupCountry.trim() || 'CH',
      pickupNotes: shippingForm.pickupNotes.trim(),
    });
  };

  const bulkProducts = useMutation(
    ({ action, ids }) => adminShopAPI.bulkProducts({ action, ids }),
    {
      onSuccess: (res, vars) => {
        toast.success(res?.message || 'Products updated');
        setSelectedIds(new Set());
        setBulkConfirm(null);
        if (vars?.action === 'delete') setMenu(null);
        invalidate();
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Could not update products'),
    }
  );

  const clearSelection = () => setSelectedIds(new Set());

  const togglePageSelection = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const toggleRowSelection = (id, checked, shiftKey) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (shiftKey && lastCheckedRef.current) {
        const a = pageIds.indexOf(lastCheckedRef.current);
        const b = pageIds.indexOf(id);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let i = lo; i <= hi; i += 1) {
            if (checked) next.add(pageIds[i]);
            else next.delete(pageIds[i]);
          }
          return next;
        }
      }
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
    lastCheckedRef.current = id;
  };

  const selectAllMatching = () => {
    setSelectedIds(new Set(filtered.map((p) => p.id)));
  };

  const runBulk = (action) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    bulkProducts.mutate({ action, ids });
  };

  const exportSelectedCsv = () => {
    const source = selectedProducts.length ? selectedProducts : filtered;
    const header = ['Name', 'Category', 'Sale Price', 'Status', 'Inventory', 'SKU', 'Variants'];
    const rows = source.map((p) => [
      p.name,
      p.categoryName || '',
      Number(p.priceChf).toFixed(2),
      p.isActive ? 'Active' : 'Inactive',
      p.trackStock ? `${p.stockQuantity} in stock` : 'Not tracked',
      p.sku || '',
      (p.variants || [])
        .map((v) => [v.size, v.colour, v.sku, `${v.stockQuantity}`].filter(Boolean).join(' '))
        .join(' | '),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = selectedProducts.length ? 'hps-shop-products-selected.csv' : 'hps-shop-products.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const setPrimaryImage = useMutation(
    ({ productId, imageId }) => adminShopAPI.setPrimaryImage(productId, imageId),
    {
      onMutate: ({ imageId }) => {
        setExistingImages((prev) => {
          const i = prev.findIndex((img) => img.id === imageId);
          return moveItem(prev, i, 0).map((img, idx) => ({ ...img, isPrimary: idx === 0 }));
        });
      },
      onSuccess: (res) => {
        const images = res?.data?.images;
        if (images) applyProductImages(images);
        toast.success('Cover photo updated');
        invalidate();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Could not set cover photo');
        invalidate();
      },
    }
  );

  const deleteImage = useMutation(
    ({ productId, imageId }) => adminShopAPI.deleteImage(productId, imageId),
    {
      onSuccess: (res) => {
        const images = res?.data?.images || [];
        applyProductImages(images);
        toast.success('Photo removed');
        invalidate();
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Could not remove photo'),
    }
  );

  const applyProductImages = (images) => {
    const next = Array.isArray(images) ? images : [];
    setExistingImages(next);
    queryClient.setQueryData(['admin-shop-products', tenantId], (old) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.map((p) =>
          p.id === editingId
            ? {
                ...p,
                images: next,
                imageCount: next.length,
                imageUrl: next.find((img) => img.isPrimary)?.url || next[0]?.url || p.imageUrl,
              }
            : p
        ),
      };
    });
  };

  const reorderImages = useMutation(
    ({ productId, imageIds }) => adminShopAPI.reorderImages(productId, imageIds),
    {
      onSuccess: (res) => {
        const images = res?.data?.images;
        if (images) applyProductImages(images);
        invalidate();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Could not reorder photos');
        invalidate();
      },
    }
  );

  const persistSavedOrder = (list) => {
    if (!editingId) return;
    const ids = list.map((img) => img.id).filter(Boolean);
    if (ids.length < 2) return;
    window.clearTimeout(persistTimerRef.current);
    persistTimerRef.current = window.setTimeout(() => {
      reorderImages.mutate({ productId: editingId, imageIds: ids });
    }, 180);
  };

  const shiftSaved = (from, delta) => {
    setExistingImages((prev) => {
      const next = moveItem(prev, from, from + delta);
      if (next !== prev) persistSavedOrder(next);
      return next;
    });
  };

  const startGalleryDrag = (kind, index, event) => {
    if (event.button != null && event.button !== 0) return;
    if (event.target.closest('button')) return;
    const listLen = kind === 'saved' ? existingImages.length : imageFiles.length;
    if (listLen < 2) return;
    event.preventDefault();
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      /* older browsers */
    }
    const start = {
      kind,
      from: index,
      over: index,
      active: false,
      startX: event.clientX,
      startY: event.clientY,
      list: kind === 'saved' ? existingImages : imageFiles,
      originIds:
        kind === 'saved' ? existingImages.map((img) => img.id).join('|') : imageFiles.map((file) => file.name).join('|'),
    };
    galleryDragRef.current = start;
    const onMove = (e) => {
      const d = galleryDragRef.current;
      if (!d || d.kind !== kind) return;
      const dist = Math.hypot(e.clientX - d.startX, e.clientY - d.startY);
      if (!d.active && dist < 6) return;
      d.active = true;
      const hit = document.elementFromPoint(e.clientX, e.clientY);
      const card = hit?.closest?.(`[data-gallery-kind="${kind}"]`);
      const over = card ? Number(card.getAttribute('data-gallery-index')) : d.from;
      if (Number.isFinite(over) && over !== d.from) {
        const next = moveItem(d.list, d.from, over);
        d.list = next;
        if (kind === 'saved') setExistingImages(next);
        else setImageFiles(next);
        d.from = over;
      }
      d.over = Number.isFinite(over) ? over : d.from;
      setGalleryDrag({ kind: d.kind, from: d.from, over: d.over, active: true });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const d = galleryDragRef.current;
      galleryDragRef.current = null;
      setGalleryDrag(null);
      if (!d?.active) return;
      if (kind === 'saved') {
        const ids = (d.list || []).map((img) => img.id).join('|');
        if (ids !== d.originIds) persistSavedOrder(d.list);
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  useEffect(() => {
    if (!galleryDrag?.active) return undefined;
    const prev = document.body.style.cursor;
    document.body.style.cursor = 'grabbing';
    return () => {
      document.body.style.cursor = prev;
    };
  }, [galleryDrag]);

  const saveCategory = useMutation(
    (name) => adminShopAPI.createCategory({ name, isActive: true, sortOrder: categories.length }),
    {
      onSuccess: () => {
        toast.success('Category created');
        setCategoryName('');
        invalidate();
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Could not save category'),
    }
  );

  const deleteCategory = useMutation((id) => adminShopAPI.deleteCategory(id), {
    onSuccess: () => {
      toast.success('Category deleted');
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Could not delete category'),
  });

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyProduct());
    setImageFiles([]);
    setExistingImages([]);
    setDragOver(false);
    setCustomHex('#043663');
    setApplySizes([]);
    setProductOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm(formFromProduct(p));
    setImageFiles([]);
    setExistingImages(productImages(p));
    setDragOver(false);
    setCustomHex('#043663');
    setApplySizes([]);
    setProductOpen(true);
    setMenu(null);
  };

  const addOption = (kind, raw) => {
    const value = normalizeOption(raw);
    if (!value) return;
    setForm((f) => ({
      ...f,
      sizes: uniqueOptions([...f.sizes, value]),
      sizeDraft: '',
    }));
    setApplySizes([value]);
  };

  const addColourToSizes = (swatch) => {
    if (!swatch?.hex && !swatch?.name) return;
    setForm((f) => {
      const targets = f.sizes.length ? applySizes.filter((s) => f.sizes.includes(s)) : [''];
      if (f.sizes.length && targets.length === 0) {
        return f;
      }
      let added = 0;
      const next = [...f.variants];
      targets.forEach((size) => {
        const key = variantComboKey(size, swatch);
        const exists = next.some(
          (v) => variantComboKey(v.size, { name: v.colour, hex: v.colourHex }) === key
        );
        if (exists) return;
        next.push(makeVariantRow(size, swatch, f.variants, f.sku, f.name));
        added += 1;
      });
      if (!added) return f;
      return {
        ...f,
        colours: coloursFromVariants(next),
        variants: next,
      };
    });
  };

  const requestAddColour = (swatch) => {
    if (!swatch?.hex && !swatch?.name) return;
    if (form.sizes.length && applySizes.length === 0) {
      toast.error('Select a size first, then add the colour.');
      return;
    }
    const targets = form.sizes.length ? applySizes : [''];
    const alreadyAll = targets.every((size) =>
      form.variants.some(
        (v) =>
          variantComboKey(v.size, { name: v.colour, hex: v.colourHex }) ===
          variantComboKey(size, swatch)
      )
    );
    if (alreadyAll) {
      toast.error(
        form.sizes.length
          ? `That colour is already added for ${targets.join(', ')}.`
          : 'That colour is already added.'
      );
      return;
    }
    addColourToSizes(swatch);
  };

  const removeOption = (kind, value) => {
    setForm((f) => {
      if (kind === 'size') {
        const sizes = f.sizes.filter((s) => s !== value);
        const variants = f.variants.filter((v) => v.size !== value);
        return {
          ...f,
          sizes,
          colours: coloursFromVariants(variants),
          variants,
        };
      }
      const variants = f.variants.filter(
        (v) => colourKey({ name: v.colour, hex: v.colourHex }) !== colourKey(value)
      );
      return {
        ...f,
        colours: coloursFromVariants(variants),
        variants,
      };
    });
    if (kind === 'size') {
      setApplySizes((prev) => prev.filter((s) => s !== value));
    }
  };

  const removeVariant = (index) => {
    setForm((f) => {
      const variants = f.variants.filter((_, i) => i !== index);
      return {
        ...f,
        colours: coloursFromVariants(variants),
        variants,
      };
    });
  };

  const updateVariant = (index, patch) => {
    setForm((f) => ({
      ...f,
      variants: f.variants.map((v, i) => (i === index ? { ...v, ...patch } : v)),
    }));
  };

  const closeProduct = () => {
    setProductOpen(false);
    setEditingId(null);
    setForm(emptyProduct());
    setImageFiles([]);
    setExistingImages([]);
    setDragOver(false);
    setCustomHex('#043663');
    setApplySizes([]);
  };

  const onSaveProduct = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Product name is required');
      return;
    }
    const nameKey = form.name.trim().toLowerCase();
    const nameTaken = products.some(
      (p) => p.id !== editingId && String(p.name || '').trim().toLowerCase() === nameKey
    );
    if (nameTaken) {
      toast.error(`A product named “${form.name.trim()}” already exists.`);
      return;
    }
    const skuKey = form.sku.trim().toLowerCase();
    const usedSkus = new Set();
    products.forEach((p) => {
      if (p.id === editingId) return;
      if (p.sku) usedSkus.add(String(p.sku).trim().toLowerCase());
      (p.variants || []).forEach((v) => {
        if (v.sku) usedSkus.add(String(v.sku).trim().toLowerCase());
      });
    });
    if (skuKey && usedSkus.has(skuKey)) {
      toast.error(`SKU “${form.sku.trim()}” is already used.`);
      return;
    }
    const variantSkus = form.variants.map((v) => String(v.sku || '').trim().toLowerCase()).filter(Boolean);
    if (new Set(variantSkus).size !== variantSkus.length) {
      toast.error('Variant SKUs must be unique.');
      return;
    }
    if (skuKey && variantSkus.includes(skuKey)) {
      toast.error('A variant SKU cannot match the product SKU.');
      return;
    }
    const takenVariant = form.variants.find(
      (v) => v.sku && usedSkus.has(String(v.sku).trim().toLowerCase())
    );
    if (takenVariant) {
      toast.error(`SKU “${takenVariant.sku.trim()}” is already used.`);
      return;
    }
    const price = Number(form.priceChf);
    if (!Number.isFinite(price) || price < 0) {
      toast.error('Enter a valid sale price (CHF ≥ 0)');
      return;
    }
    saveProduct.mutate({
      id: editingId,
      files: imageFiles,
      payload: {
        name: form.name.trim(),
        description: form.description || null,
        categoryId: form.categoryId || null,
        priceChf: price,
        compareAtChf: form.compareAtChf === '' ? null : form.compareAtChf,
        stockQuantity: parseInt(form.stockQuantity, 10) || 0,
        sku: form.sku.trim() || null,
        trackStock: !!form.trackStock,
        isActive: !!form.isActive,
        freeShipping: !!form.freeShipping,
        sortOrder: parseInt(form.sortOrder, 10) || 0,
        variants: form.variants.map((v, i) => ({
          id: v.id || undefined,
          size: v.size,
          colour: v.colour,
          colourHex: v.colourHex || null,
          sku: String(v.sku || '').trim() || null,
          stockQuantity: parseInt(v.stockQuantity, 10) || 0,
          priceChf: v.priceChf === '' ? null : v.priceChf,
          isActive: v.isActive !== false,
          sortOrder: i,
        })),
      },
    });
  };

  const exportCsv = () => {
    const header = ['Name', 'Category', 'Sale Price', 'Status', 'Inventory', 'SKU', 'Variants'];
    const rows = filtered.map((p) => [
      p.name,
      p.categoryName || '',
      Number(p.priceChf).toFixed(2),
      p.isActive ? 'Active' : 'Inactive',
      p.trackStock ? `${p.stockQuantity} in stock` : 'Not tracked',
      p.sku || '',
      (p.variants || [])
        .map((v) =>
          [v.size, v.colour, v.sku, `${v.stockQuantity}`].filter(Boolean).join(' ')
        )
        .join(' | '),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'hps-shop-products.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Page>
      <Header>
        <Title>{shopView === 'shipping' ? 'Shipping' : 'Products'}</Title>
        <HeaderActions>
          {shopView === 'shipping' ? (
            <>
              <PillBtn type="button" onClick={() => setShopView('products')}>
                Products
              </PillBtn>
              {canEdit && (
                <PillBtn
                  type="button"
                  $primary
                  disabled={saveShipping.isLoading}
                  onClick={onSaveShipping}
                >
                  {saveShipping.isLoading ? 'Saving…' : 'Save shipping'}
                </PillBtn>
              )}
            </>
          ) : (
            <>
              <PillBtn type="button" onClick={() => setShopView('shipping')}>
                Shipping
              </PillBtn>
              <PillBtn type="button" onClick={() => setCategoryOpen(true)}>
                Categories
              </PillBtn>
              <PillBtn type="button" onClick={exportCsv} disabled={filtered.length === 0}>
                Export CSV
              </PillBtn>
              {canCreate && (
                <PillBtn type="button" $primary onClick={openCreate}>
                  + Add Product
                </PillBtn>
              )}
            </>
          )}
        </HeaderActions>
      </Header>

      {shopView === 'shipping' ? (
        <form onSubmit={onSaveShipping}>
          <SettingCard>
            <SettingHead>
              <div>
                <SettingTitle>Home delivery</SettingTitle>
                <SettingCopy>
                  Charged at checkout unless the product is marked free shipping, or the order reaches
                  the optional free-shipping threshold.
                </SettingCopy>
              </div>
              <Toggle
                type="button"
                $on={shippingForm.deliveryEnabled}
                disabled={!canEdit}
                aria-label={shippingForm.deliveryEnabled ? 'Home delivery on' : 'Home delivery off'}
                onClick={() =>
                  canEdit &&
                  setShippingForm((s) => ({ ...s, deliveryEnabled: !s.deliveryEnabled }))
                }
              />
            </SettingHead>
            <Grid>
              <Field>
                <Label htmlFor="shop-delivery-fee">Delivery fee (CHF)</Label>
                <Input
                  id="shop-delivery-fee"
                  type="number"
                  min="0"
                  step="0.01"
                  value={shippingForm.deliveryFeeChf}
                  disabled={!canEdit || !shippingForm.deliveryEnabled}
                  onChange={(e) => setShippingForm((s) => ({ ...s, deliveryFeeChf: e.target.value }))}
                />
              </Field>
              <Field>
                <Label htmlFor="shop-free-ship-min">Free shipping from (CHF)</Label>
                <Input
                  id="shop-free-ship-min"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Optional"
                  value={shippingForm.freeShippingMinChf}
                  disabled={!canEdit || !shippingForm.deliveryEnabled}
                  onChange={(e) =>
                    setShippingForm((s) => ({ ...s, freeShippingMinChf: e.target.value }))
                  }
                />
                <Hint>Leave empty if there is no order-value threshold.</Hint>
              </Field>
            </Grid>
          </SettingCard>

          <SettingCard>
            <SettingHead>
              <div>
                <SettingTitle>Pickup</SettingTitle>
                <SettingCopy>
                  Buyers can collect the order at this address instead of paying for delivery.
                </SettingCopy>
              </div>
              <Toggle
                type="button"
                $on={shippingForm.pickupEnabled}
                disabled={!canEdit}
                aria-label={shippingForm.pickupEnabled ? 'Pickup on' : 'Pickup off'}
                onClick={() =>
                  canEdit && setShippingForm((s) => ({ ...s, pickupEnabled: !s.pickupEnabled }))
                }
              />
            </SettingHead>
            <Grid>
              <Field className="full">
                <Label htmlFor="shop-pickup-name">Location name</Label>
                <Input
                  id="shop-pickup-name"
                  placeholder="e.g. HPS office, Lausanne"
                  value={shippingForm.pickupName}
                  disabled={!canEdit || !shippingForm.pickupEnabled}
                  onChange={(e) => setShippingForm((s) => ({ ...s, pickupName: e.target.value }))}
                />
              </Field>
              <Field className="full">
                <Label htmlFor="shop-pickup-street">Street</Label>
                <Input
                  id="shop-pickup-street"
                  value={shippingForm.pickupStreet}
                  disabled={!canEdit || !shippingForm.pickupEnabled}
                  onChange={(e) => setShippingForm((s) => ({ ...s, pickupStreet: e.target.value }))}
                />
              </Field>
              <Field>
                <Label htmlFor="shop-pickup-postal">Postal code</Label>
                <Input
                  id="shop-pickup-postal"
                  value={shippingForm.pickupPostalCode}
                  disabled={!canEdit || !shippingForm.pickupEnabled}
                  onChange={(e) =>
                    setShippingForm((s) => ({ ...s, pickupPostalCode: e.target.value }))
                  }
                />
              </Field>
              <Field>
                <Label htmlFor="shop-pickup-city">City</Label>
                <Input
                  id="shop-pickup-city"
                  value={shippingForm.pickupCity}
                  disabled={!canEdit || !shippingForm.pickupEnabled}
                  onChange={(e) => setShippingForm((s) => ({ ...s, pickupCity: e.target.value }))}
                />
              </Field>
              <Field>
                <Label htmlFor="shop-pickup-country">Country</Label>
                <Input
                  id="shop-pickup-country"
                  value={shippingForm.pickupCountry}
                  disabled={!canEdit || !shippingForm.pickupEnabled}
                  onChange={(e) => setShippingForm((s) => ({ ...s, pickupCountry: e.target.value }))}
                />
              </Field>
              <Field className="full">
                <Label htmlFor="shop-pickup-notes">Notes for buyers</Label>
                <TextArea
                  id="shop-pickup-notes"
                  style={{ minHeight: 88 }}
                  placeholder="Opening hours, who to ask for, parking…"
                  value={shippingForm.pickupNotes}
                  disabled={!canEdit || !shippingForm.pickupEnabled}
                  onChange={(e) => setShippingForm((s) => ({ ...s, pickupNotes: e.target.value }))}
                />
              </Field>
            </Grid>
          </SettingCard>

          <SettingCard>
            <SettingTitle>Order notices</SettingTitle>
            <SettingCopy style={{ marginTop: 6 }}>
              New shop orders will be emailed to the tenant contact address
              {shippingContactEmail ? (
                <>
                  : <strong>{shippingContactEmail}</strong>.
                </>
              ) : (
                ' (set under Settings → General).'
              )}{' '}
              Checkout will collect the buyer’s delivery address when they choose home delivery.
            </SettingCopy>
          </SettingCard>
        </form>
      ) : (
        <>
      <Tabs>
        {[
          ['all', 'All Products'],
          ['active', 'Active'],
          ['inactive', 'Inactive'],
          ['oos', 'Out of stock'],
        ].map(([id, label]) => (
          <Tab
            key={id}
            type="button"
            $active={tab === id}
            onClick={() => {
              setTab(id);
              setPage(1);
            }}
          >
            {label}
          </Tab>
        ))}
      </Tabs>

      <Toolbar>
        <ToolbarLeft>
          <strong style={{ color: '#111827' }}>{filtered.length} Products</strong>
          <span>
            Show{' '}
            <Select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Select>
          </span>
          <span>
            Showing {from} - {to} of {filtered.length}
          </span>
        </ToolbarLeft>
        <ToolbarRight>
          <Select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Filter</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Search
            placeholder="Search Products"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </ToolbarRight>
      </Toolbar>

      {selectedCount > 0 && (
        <BulkBar>
          <BulkLeft>
            <span>
              {selectedCount === filtered.length
                ? `${selectedCount} selected`
                : `${selectedCount} of ${filtered.length} selected`}
            </span>
            {!allFilteredSelected && filtered.length > pageIds.length ? (
              <LinkBtn type="button" onClick={selectAllMatching}>
                Select all {filtered.length} matching
              </LinkBtn>
            ) : null}
            <LinkBtn type="button" onClick={clearSelection}>
              Clear
            </LinkBtn>
          </BulkLeft>
          <BulkActions>
            <PillBtn type="button" $ghost onClick={exportSelectedCsv}>
              Export
            </PillBtn>
            {canEdit && (
              <>
                <PillBtn
                  type="button"
                  disabled={!canBulkActivate || bulkProducts.isLoading}
                  onClick={() => runBulk('activate')}
                >
                  Activate
                </PillBtn>
                <PillBtn
                  type="button"
                  $ghost
                  disabled={!canBulkDeactivate || bulkProducts.isLoading}
                  onClick={() => runBulk('deactivate')}
                >
                  Deactivate
                </PillBtn>
              </>
            )}
            {canDelete && (
              <PillBtn
                type="button"
                $danger
                disabled={bulkProducts.isLoading}
                onClick={() => setBulkConfirm('delete')}
              >
                Delete
              </PillBtn>
            )}
          </BulkActions>
        </BulkBar>
      )}

      {isLoading ? (
        <Empty>Loading products…</Empty>
      ) : slice.length === 0 ? (
        <Empty>No products match this view. Add a product to get started.</Empty>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <Check
                    ref={headerCheckRef}
                    aria-label="Select all products on this page"
                    checked={allPageSelected}
                    onChange={(e) => togglePageSelection(e.target.checked)}
                  />
                </th>
                <th>Product Name</th>
                <th>Image</th>
                <th>Sale Price</th>
                <th>Status</th>
                <th>Inventory</th>
                <th>SKU</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {slice.map((p) => (
                <tr key={p.id} className={selectedIds.has(p.id) ? 'selected' : undefined}>
                  <td>
                    <Check
                      aria-label={`Select ${p.name}`}
                      checked={selectedIds.has(p.id)}
                      onChange={(e) => toggleRowSelection(p.id, e.target.checked, e.nativeEvent.shiftKey)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <NameCell>
                      {p.name}
                      {p.freeShipping ? <QuietTag>Free shipping</QuietTag> : null}
                      {p.variantCount > 0 ? (
                        <SwatchRow>
                          {(p.colourSwatches || []).map((c) => {
                            const swatch = swatchFrom(c.name, c.hex);
                            return swatch ? (
                              <Swatch
                                key={swatch.hex || swatch.name}
                                $color={swatch.hex}
                                $size={14}
                                $light={isLightHex(swatch.hex)}
                                title={swatch.name}
                              />
                            ) : null;
                          })}
                          {p.sizes?.length ? (
                            <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 400 }}>
                              {p.sizes.join(', ')}
                            </span>
                          ) : null}
                        </SwatchRow>
                      ) : null}
                    </NameCell>
                  </td>
                  <td>
                    <ThumbWrap>
                      <Thumb>
                        {p.imageUrl ? <img src={p.imageUrl} alt="" /> : null}
                      </Thumb>
                      {(p.imageCount || productImages(p).length) > 1 ? (
                        <ThumbCount>{p.imageCount || productImages(p).length}</ThumbCount>
                      ) : null}
                    </ThumbWrap>
                  </td>
                  <td>
                    {p.variantCount > 0 && Number(p.priceFromChf) !== Number(p.priceChf)
                      ? `from ${money(p.priceFromChf)}`
                      : money(p.priceChf)}
                  </td>
                  <td>
                    <Toggle
                      type="button"
                      $on={p.isActive}
                      disabled={!canEdit}
                      aria-label={p.isActive ? 'Active' : 'Inactive'}
                      onClick={() => canEdit && toggleActive.mutate({ id: p.id, isActive: !p.isActive })}
                    />
                  </td>
                  <td>
                    {p.trackStock
                      ? p.variantCount
                        ? `${p.stockQuantity} in stock · ${p.variantCount} variants`
                        : `${p.stockQuantity} in stock`
                      : 'Not tracked'}
                  </td>
                  <td>{p.sku || '—'}</td>
                  <td>
                    <Kebab
                      type="button"
                      aria-label="Actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (menu?.id === p.id) {
                          setMenu(null);
                          return;
                        }
                        const rect = e.currentTarget.getBoundingClientRect();
                        const width = 148;
                        const height = 88;
                        const left = Math.min(rect.right - width, window.innerWidth - width - 8);
                        const top =
                          rect.bottom + 4 + height > window.innerHeight
                            ? Math.max(8, rect.top - height - 4)
                            : rect.bottom + 4;
                        setMenu({
                          id: p.id,
                          top,
                          left: Math.max(8, left),
                        });
                      }}
                    >
                      ⋯
                    </Kebab>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {menu &&
        createPortal(
          <Menu ref={menuRef} $top={menu.top} $left={menu.left}>
            {canEdit && (
              <button
                type="button"
                onClick={() => {
                  const product = products.find((item) => item.id === menu.id);
                  if (product) openEdit(product);
                }}
              >
                Edit
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="danger"
                onClick={() => {
                  const product = products.find((item) => item.id === menu.id);
                  setMenu(null);
                  if (product && window.confirm(`Delete “${product.name}”?`)) {
                    deleteProduct.mutate(product.id);
                  }
                }}
              >
                Delete
              </button>
            )}
          </Menu>,
          document.body
        )}

      {filtered.length > 0 && (
        <Pager>
          <PageBtn type="button" disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>
            ← Previous
          </PageBtn>
          {pageList(safePage, totalPages).map((item, i) =>
            item === '…' ? (
              <span key={`e${i}`} style={{ color: '#9ca3af', padding: '0 0.2rem' }}>
                …
              </span>
            ) : (
              <PageBtn key={item} type="button" $active={item === safePage} onClick={() => setPage(item)}>
                {item}
              </PageBtn>
            )
          )}
          <PageBtn type="button" disabled={safePage === totalPages} onClick={() => setPage(safePage + 1)}>
            Next →
          </PageBtn>
        </Pager>
      )}
        </>
      )}

      {bulkConfirm === 'delete' &&
        createPortal(
          <Overlay onClick={() => !bulkProducts.isLoading && setBulkConfirm(null)}>
            <ConfirmBox onClick={(e) => e.stopPropagation()}>
              <ConfirmTitle>
                Delete {selectedCount} product{selectedCount === 1 ? '' : 's'}?
              </ConfirmTitle>
              <ConfirmCopy>
                {selectedCount === 1
                  ? `“${selectedProducts[0]?.name || 'This product'}” and its photos will be permanently removed.`
                  : `${selectedCount} products and their photos will be permanently removed.`}{' '}
                This cannot be undone.
              </ConfirmCopy>
              <ModalFoot style={{ marginTop: 0 }}>
                <GhostBtn
                  type="button"
                  disabled={bulkProducts.isLoading}
                  onClick={() => setBulkConfirm(null)}
                >
                  Cancel
                </GhostBtn>
                <PillBtn
                  type="button"
                  $danger
                  disabled={bulkProducts.isLoading}
                  onClick={() => runBulk('delete')}
                >
                  {bulkProducts.isLoading ? 'Deleting…' : 'Delete'}
                </PillBtn>
              </ModalFoot>
            </ConfirmBox>
          </Overlay>,
          document.body
        )}

      {productOpen && (
        <Overlay onClick={closeProduct}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>{editingId ? 'Edit product' : 'Add product'}</ModalTitle>
              <CloseBtn type="button" onClick={closeProduct} aria-label="Close">
                ×
              </CloseBtn>
            </ModalHead>
            <ModalBody onSubmit={onSaveProduct}>
              <Grid>
                <Field className="full">
                  <Label htmlFor="shop-name">Product name</Label>
                  <Input
                    id="shop-name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="HPS Hoodie"
                    autoFocus
                  />
                </Field>
                <Field>
                  <Label htmlFor="shop-cat">Category</Label>
                  <FieldSelect
                    id="shop-cat"
                    value={form.categoryId}
                    onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
                  >
                    <option value="">Uncategorized</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </FieldSelect>
                </Field>
                <Field>
                  <Label htmlFor="shop-sku">SKU</Label>
                  <Input
                    id="shop-sku"
                    value={form.sku}
                    onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
                    placeholder="HPS-HD-001"
                  />
                </Field>
                <Field>
                  <Label htmlFor="shop-price">Sale price (CHF)</Label>
                  <Input
                    id="shop-price"
                    type="number"
                    min="0"
                    step="0.05"
                    value={form.priceChf}
                    onChange={(e) => setForm((f) => ({ ...f, priceChf: e.target.value }))}
                    placeholder="79.00"
                  />
                </Field>
                <Field>
                  <Label htmlFor="shop-compare">Compare-at price</Label>
                  <Input
                    id="shop-compare"
                    type="number"
                    min="0"
                    step="0.05"
                    value={form.compareAtChf}
                    onChange={(e) => setForm((f) => ({ ...f, compareAtChf: e.target.value }))}
                    placeholder="Optional"
                  />
                </Field>
                <Field>
                  <Label htmlFor="shop-stock">Inventory</Label>
                  <Input
                    id="shop-stock"
                    type="number"
                    min="0"
                    step="1"
                    value={
                      form.variants.length
                        ? String(
                            form.variants.reduce(
                              (sum, v) => sum + (parseInt(v.stockQuantity, 10) || 0),
                              0
                            )
                          )
                        : form.stockQuantity
                    }
                    disabled={form.variants.length > 0}
                    onChange={(e) => setForm((f) => ({ ...f, stockQuantity: e.target.value }))}
                  />
                  {form.variants.length > 0 ? (
                    <Hint>Stock is set on each size/colour row below.</Hint>
                  ) : null}
                </Field>
                <Field className="full">
                  <PhotoHead>
                    <Label htmlFor="shop-image">Photos</Label>
                    <PhotoCount>
                      {existingImages.length + imageFiles.length}/{MAX_PRODUCT_IMAGES}
                      {reorderImages.isLoading ? <SavingNote> · Saving order</SavingNote> : null}
                    </PhotoCount>
                  </PhotoHead>
                  {existingImages.length + imageFiles.length > 1 ? (
                    <GalleryHint>
                      {imageFiles.length > 0 && existingImages.length > 0
                        ? 'Drag to reorder. The first photo is the cover — new photos stay at the end until you save.'
                        : 'Drag to reorder. The first photo is the cover.'}
                    </GalleryHint>
                  ) : (
                    <GalleryHint>Add photos. The first one becomes the cover.</GalleryHint>
                  )}
                  {(existingImages.length > 0 || imageFiles.length > 0) && (
                    <GalleryGrid role="list">
                      {existingImages.map((img, index) => {
                        const isCover = index === 0;
                        const canDragSaved =
                          existingImages.length > 1 && existingImages.every((item) => item.id);
                        const dragging =
                          galleryDrag?.active && galleryDrag.kind === 'saved' && galleryDrag.from === index;
                        const over =
                          galleryDrag?.active && galleryDrag.kind === 'saved' && galleryDrag.over === index && !dragging;
                        return (
                          <GalleryCard
                            key={img.id || img.url}
                            role="listitem"
                            data-gallery-kind="saved"
                            data-gallery-index={index}
                            $primary={isCover}
                            $hero={isCover && existingImages.length + imageFiles.length > 1}
                            $canDrag={canDragSaved}
                            $dragging={dragging}
                            $over={over}
                            tabIndex={canDragSaved ? 0 : undefined}
                            aria-grabbed={dragging ? 'true' : 'false'}
                            aria-label={`Photo ${index + 1}${isCover ? ', cover' : ''}. ${
                              canDragSaved ? 'Drag or use arrow keys to reorder.' : ''
                            }`}
                            onPointerDown={(e) => startGalleryDrag('saved', index, e)}
                            onKeyDown={(e) => {
                              if (!canDragSaved) return;
                              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                                e.preventDefault();
                                shiftSaved(index, -1);
                              }
                              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                                e.preventDefault();
                                shiftSaved(index, 1);
                              }
                            }}
                          >
                            <GalleryPhoto>
                              <img src={img.url} alt="" draggable={false} />
                              {isCover ? <MainBadge>Cover</MainBadge> : null}
                              {editingId && img.id ? (
                                <GalleryX
                                  type="button"
                                  aria-label="Remove photo"
                                  disabled={deleteImage.isLoading}
                                  onClick={() =>
                                    deleteImage.mutate({ productId: editingId, imageId: img.id })
                                  }
                                >
                                  ×
                                </GalleryX>
                              ) : null}
                              <IndexBadge>{index + 1}</IndexBadge>
                              {canDragSaved ? (
                                <Grip>
                                  <GripIcon />
                                </Grip>
                              ) : null}
                            </GalleryPhoto>
                            {editingId && img.id && !isCover ? (
                              <GalleryActions>
                                <GalleryBtn
                                  type="button"
                                  $primary
                                  disabled={setPrimaryImage.isLoading}
                                  onClick={() =>
                                    setPrimaryImage.mutate({ productId: editingId, imageId: img.id })
                                  }
                                >
                                  Set cover
                                </GalleryBtn>
                              </GalleryActions>
                            ) : (
                              <GalleryName>{isCover ? 'Cover photo' : 'Photo'}</GalleryName>
                            )}
                          </GalleryCard>
                        );
                      })}
                      {imageFiles.map((file, index) => (
                        <PendingThumb
                          key={`${file.name}-${file.lastModified}-${index}`}
                          file={file}
                          index={index}
                          label={existingImages.length + index + 1}
                          canDrag={imageFiles.length > 1}
                          dragging={
                            galleryDrag?.active && galleryDrag.kind === 'pending' && galleryDrag.from === index
                          }
                          over={
                            galleryDrag?.active &&
                            galleryDrag.kind === 'pending' &&
                            galleryDrag.over === index &&
                            galleryDrag.from !== index
                          }
                          hero={existingImages.length === 0 && index === 0 && imageFiles.length > 1}
                          onDragStart={(e) => startGalleryDrag('pending', index, e)}
                          onKeyMove={(delta) =>
                            setImageFiles((prev) => moveItem(prev, index, index + delta))
                          }
                          onRemove={() =>
                            setImageFiles((prev) => prev.filter((_, i) => i !== index))
                          }
                        />
                      ))}
                    </GalleryGrid>
                  )}
                  {existingImages.length + imageFiles.length < MAX_PRODUCT_IMAGES ? (
                    <DropZone
                      $over={dragOver}
                      onClick={() => imageInputRef.current?.click()}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOver(true);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOver(false);
                        addImageFiles(e.dataTransfer?.files);
                      }}
                    >
                      <HiddenFile
                        id="shop-image"
                        ref={imageInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        multiple
                        onChange={(e) => {
                          addImageFiles(e.target.files);
                          e.target.value = '';
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <UploadBtn
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          imageInputRef.current?.click();
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path
                            d="M12 16V4m0 0l-4 4m4-4l4 4M4 16.5V18a2 2 0 002 2h12a2 2 0 002-2v-1.5"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Upload
                      </UploadBtn>
                      <DropTitle>Choose a file or drag & drop it here</DropTitle>
                      <DropHint>
                        Up to {MAX_PRODUCT_IMAGES} photos · PNG, JPG or WEBP · 5 MB each
                      </DropHint>
                    </DropZone>
                  ) : (
                    <Hint>Maximum of {MAX_PRODUCT_IMAGES} photos reached.</Hint>
                  )}
                </Field>
                <Field className="full">
                  <Checks>
                    <label>
                      <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                      />
                      Active
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={form.trackStock}
                        onChange={(e) => setForm((f) => ({ ...f, trackStock: e.target.checked }))}
                      />
                      Track stock
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={!!form.freeShipping}
                        onChange={(e) => setForm((f) => ({ ...f, freeShipping: e.target.checked }))}
                      />
                      Free shipping
                    </label>
                  </Checks>
                </Field>
                <Field className="full">
                  <Label>Sizes / colours</Label>
                  <VariantBox>
                    <Label htmlFor="shop-size-draft">Sizes</Label>
                    <ChipRow style={{ marginTop: 6 }}>
                      {form.sizes.map((size) => (
                        <Chip key={size}>
                          {size}
                          <button type="button" aria-label={`Remove ${size}`} onClick={() => removeOption('size', size)}>
                            ×
                          </button>
                        </Chip>
                      ))}
                      <Input
                        id="shop-size-draft"
                        value={form.sizeDraft}
                        placeholder="Add size"
                        style={{ width: 110, height: 28, padding: '0 0.6rem' }}
                        onChange={(e) => setForm((f) => ({ ...f, sizeDraft: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addOption('size', form.sizeDraft);
                          }
                        }}
                      />
                      {SIZE_PRESETS.filter((s) => !form.sizes.includes(s)).map((size) => (
                        <SuggestBtn key={size} type="button" onClick={() => addOption('size', size)}>
                          {size}
                        </SuggestBtn>
                      ))}
                    </ChipRow>
                    {form.sizes.length > 0 ? (
                      <>
                        <Label style={{ marginTop: 12 }}>Apply colour to</Label>
                        <ChipRow style={{ marginTop: 6 }}>
                          <SuggestBtn
                            type="button"
                            $active={form.sizes.length > 0 && applySizes.length === form.sizes.length}
                            onClick={() =>
                              setApplySizes((prev) =>
                                prev.length === form.sizes.length ? [] : [...form.sizes]
                              )
                            }
                          >
                            All sizes
                          </SuggestBtn>
                          {form.sizes.map((size) => (
                            <SuggestBtn
                              key={size}
                              type="button"
                              $active={applySizes.includes(size)}
                              onClick={() =>
                                setApplySizes((prev) =>
                                  prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]
                                )
                              }
                            >
                              {size}
                            </SuggestBtn>
                          ))}
                        </ChipRow>
                        <Hint>Pick one or more sizes, then click a colour. It will not be added to the other sizes.</Hint>
                      </>
                    ) : (
                      <Hint style={{ marginTop: 10 }}>Add a size first if this colour should belong to a specific size.</Hint>
                    )}
                    <Label style={{ marginTop: 12 }}>Colours</Label>
                    {form.colours.length > 0 ? (
                      <SelectedSwatches>
                        {form.colours.map((colour) => (
                          <SelectedSwatch key={colourKey(colour)}>
                            <Swatch
                              $color={colour.hex}
                              $size={36}
                              $light={isLightHex(colour.hex)}
                            />
                            <SwatchRemove
                              type="button"
                              aria-label={`Remove ${colour.name}`}
                              onClick={() => removeOption('colour', colour)}
                            >
                              ×
                            </SwatchRemove>
                            <span title={colour.name}>{colour.name}</span>
                          </SelectedSwatch>
                        ))}
                      </SelectedSwatches>
                    ) : null}
                    <SwatchGrid>
                      {COLOUR_PALETTE.map((swatch) => {
                        const selected = form.colours.some((c) => colourKey(c) === colourKey(swatch));
                        return (
                          <SwatchPick
                            key={swatch.hex}
                            type="button"
                            title={swatch.name}
                            aria-label={swatch.name}
                            aria-pressed={selected}
                            $color={swatch.hex}
                            $light={isLightHex(swatch.hex)}
                            $selected={selected}
                            onClick={() => requestAddColour(swatch)}
                          />
                        );
                      })}
                    </SwatchGrid>
                    <CustomColourRow>
                      <input
                        type="color"
                        aria-label="Pick a custom colour"
                        value={customHex}
                        onChange={(e) => setCustomHex(e.target.value)}
                      />
                      <Swatch
                        $color={customHex}
                        $size={28}
                        $light={isLightHex(customHex)}
                        title={customHex}
                      />
                      <PillBtn
                        type="button"
                        onClick={() => {
                          const swatch = swatchFrom(null, customHex);
                          if (!swatch) return;
                          const already = form.colours.some((c) => colourKey(c) === colourKey(swatch));
                          if (already) {
                            toast.error('That colour is already added.');
                            return;
                          }
                          requestAddColour(swatch);
                        }}
                      >
                        Add colour
                      </PillBtn>
                    </CustomColourRow>
                    <Hint>Drag the picker to preview, then click Add colour for the selected size(s).</Hint>
                    {form.variants.length === 0 ? (
                      <Hint>Add sizes, colours, or both to generate sellable combinations.</Hint>
                    ) : (
                      <VariantTable>
                        <thead>
                          <tr>
                            <th>Size</th>
                            <th>Colour</th>
                            <th>SKU</th>
                            <th>Stock</th>
                            <th>Price</th>
                            <th>Active</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.variants.map((v, i) => (
                            <tr key={`${v.size || ''}-${v.colour || ''}-${v.id || i}`}>
                              <td>{v.size || '—'}</td>
                              <td>
                                {v.colour ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    <Swatch
                                      $color={v.colourHex || swatchFrom(v.colour)?.hex || '#9CA3AF'}
                                      $size={16}
                                      $light={isLightHex(v.colourHex || swatchFrom(v.colour)?.hex)}
                                    />
                                    {v.colour}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={v.sku}
                                  onChange={(e) => updateVariant(i, { sku: e.target.value })}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={v.stockQuantity}
                                  onChange={(e) => updateVariant(i, { stockQuantity: e.target.value })}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.05"
                                  placeholder="Same"
                                  value={v.priceChf}
                                  onChange={(e) => updateVariant(i, { priceChf: e.target.value })}
                                />
                              </td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={v.isActive !== false}
                                  onChange={(e) => updateVariant(i, { isActive: e.target.checked })}
                                  aria-label={`Active ${[v.size, v.colour].filter(Boolean).join(' ')}`}
                                />
                              </td>
                              <td>
                                <GhostBtn
                                  type="button"
                                  onClick={() => removeVariant(i)}
                                  aria-label={`Remove ${[v.size, v.colour].filter(Boolean).join(' ')}`}
                                  style={{ height: 28, padding: '0 0.6rem', fontSize: '0.75rem' }}
                                >
                                  Remove
                                </GhostBtn>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </VariantTable>
                    )}
                  </VariantBox>
                </Field>
                <Field className="full">
                  <Label>Description</Label>
                  <TextArea
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Product description"
                  />
                </Field>
              </Grid>
              <ModalFoot>
                <GhostBtn type="button" onClick={closeProduct}>
                  Cancel
                </GhostBtn>
                <PillBtn type="submit" $primary disabled={saveProduct.isLoading}>
                  {editingId ? 'Save changes' : 'Add product'}
                </PillBtn>
              </ModalFoot>
            </ModalBody>
          </Modal>
        </Overlay>
      )}

      {categoryOpen && (
        <Overlay onClick={() => setCategoryOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <ModalHead>
              <ModalTitle>Categories</ModalTitle>
              <CloseBtn type="button" onClick={() => setCategoryOpen(false)} aria-label="Close">
                ×
              </CloseBtn>
            </ModalHead>
            <ModalBody
              onSubmit={(e) => {
                e.preventDefault();
                if (!categoryName.trim()) {
                  toast.error('Category name is required');
                  return;
                }
                const taken = categories.some(
                  (c) => String(c.name || '').trim().toLowerCase() === categoryName.trim().toLowerCase()
                );
                if (taken) {
                  toast.error(`A category named “${categoryName.trim()}” already exists.`);
                  return;
                }
                saveCategory.mutate(categoryName.trim());
              }}
            >
              <Field>
                <Label htmlFor="cat-name">New category</Label>
                <Input
                  id="cat-name"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  placeholder="Apparel"
                />
              </Field>
              <ModalFoot>
                <PillBtn type="submit" $primary disabled={saveCategory.isLoading}>
                  Add category
                </PillBtn>
              </ModalFoot>
              <div style={{ marginTop: '1rem' }}>
                {categories.length === 0 ? (
                  <Empty style={{ padding: '1rem 0' }}>No categories yet.</Empty>
                ) : (
                  categories.map((c) => (
                    <div
                      key={c.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.65rem 0',
                        borderBottom: '1px solid #f3f4f6',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>{c.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                          {c.productCount} products
                        </div>
                      </div>
                      {canDelete && (
                        <GhostBtn
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Delete category “${c.name}”?`)) {
                              deleteCategory.mutate(c.id);
                            }
                          }}
                        >
                          Delete
                        </GhostBtn>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ModalBody>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
}
